import type { BuyingOrderRow } from '@erp/rpc';
import { buyerCodesLabel } from './buyerCodesLabel';
import type { FilterableColumn } from './columnsHiddenByFilters';

type ColumnRow = Pick<
  BuyingOrderRow,
  | 'kind'
  | 'supplierId'
  | 'supplierName'
  | 'sourceWarehouseId'
  | 'sourceWarehouseName'
  | 'sourceWarehouseCode'
  | 'shipToWarehouseId'
  | 'shipToWarehouseName'
  | 'shipToWarehouseCode'
  | 'buyerCodes'
>;

export interface ActiveOrderFilters {
  vendorIds: readonly string[];
  buyerCodes: readonly string[];
  /** Destination warehouses picked; empty when every one in scope is. */
  warehouseIds: readonly string[];
  /** Vendor grouping folds the ship-tos into a location count. */
  vendorGrouping: boolean;
}

const warehouseText = (code: string | null, name: string | null) =>
  code ?? name ?? '';

/** The Orders table's columns a board filter can narrow to one value. */
export function orderFilterColumns(
  filters: ActiveOrderFilters,
): FilterableColumn<ColumnRow>[] {
  return [
    {
      id: 'vendor',
      filtered: filters.vendorIds.length > 0,
      singlePick: filters.vendorIds.length === 1,
      key: (r) =>
        r.kind === 'to'
          ? `to:${r.sourceWarehouseId ?? ''}|${r.shipToWarehouseId ?? ''}`
          : `po:${r.supplierId ?? ''}`,
      label: (r) =>
        r.kind === 'to'
          ? `${warehouseText(r.sourceWarehouseCode, r.sourceWarehouseName)} → ${warehouseText(r.shipToWarehouseCode, r.shipToWarehouseName)}`
          : (r.supplierName ?? ''),
    },
    {
      id: 'shipTo',
      // A destination pick does not pin the ship-to: a pivot ships via a hub.
      filtered: !filters.vendorGrouping && filters.warehouseIds.length > 0,
      key: (r) => r.shipToWarehouseId ?? '',
      label: (r) => warehouseText(r.shipToWarehouseCode, r.shipToWarehouseName),
    },
    {
      id: 'buyer',
      filtered: filters.buyerCodes.length > 0,
      // The buyer filter drops other buyers' lines before rows are grouped.
      singlePick: filters.buyerCodes.length === 1,
      key: (r) => buyerCodesLabel(r.buyerCodes) ?? '',
      label: (r) => buyerCodesLabel(r.buyerCodes) ?? '',
    },
  ];
}
